import React from "react";
import { t } from "../../i18n";
import { Sparkles } from "lucide-react";
import SegmentCard from "../../components/SegmentCard.jsx";
import MediaSelector from "../../components/MediaSelector.jsx";

/**
 * StoryboardBrief – Storyboard form shown when montage is created but has no segments yet.
 *
 * Props:
 * - montage: object
 * - loading: boolean
 * - onUpdate: function(field, value)
 * - onGenerateStoryboard: function
 */
export function StoryboardBrief({ montage, loading, onUpdate, onGenerateStoryboard }) {
    return (
        <section className="montage-card">
            <div className="montage-step-content">
                <h2>{t("montage.step.storyboard")}</h2>
                <p className="montage-step-description">
                    {t("montage.storyboard.descBrief")}
                </p>

                <div className="montage-storyboard-form">
                    <div className="montage-form-group">
                        <label>{t("montage.label.projectDesc")}</label>
                        <textarea
                            value={montage.description}
                            onChange={(e) => onUpdate("description", e.target.value)}
                            placeholder={t("montage.placeholder.desc")}
                            rows={6}
                            className="montage-textarea"
                        />
                    </div>

                    <div className="montage-form-row">
                        <div className="montage-form-group">
                            <label>{t("montage.label.targetDuration")}</label>
                            <input
                                type="number"
                                value={montage.target_duration}
                                onChange={(e) => onUpdate("target_duration", parseInt(e.target.value) || 30)}
                                min={5}
                                max={120}
                                className="montage-input"
                            />
                        </div>
                        <div className="montage-form-group">
                            <label>{t("montage.label.estimatedSegments")}</label>
                            <input
                                type="number"
                                value={Math.max(2, Math.round(montage.target_duration / 10))}
                                disabled
                                className="montage-input"
                                title="Auto-calculé (~10s par segment)"
                            />
                        </div>
                    </div>

                    <button
                        type="button"
                        className="montage-btn montage-btn-primary"
                        disabled={!montage.description.trim() || loading}
                        onClick={onGenerateStoryboard}
                    >
                        <Sparkles size={16} />
                        {loading ? t("montage.btn.generating") : t("montage.btn.generateStoryboard")}
                    </button>
                </div>
            </div>
        </section>
    );
}

/**
 * StoryboardStep – Full storyboard step content with segments list and editing.
 *
 * Props:
 * - montage: object
 * - loading: boolean
 * - enhancingPrompts: boolean
 * - onUpdate: function(field, value)
 * - onGenerateStoryboard: function
 * - onUpdateSegment: function(index, segment)
 * - onDeleteSegment: function(index)
 * - onRegenerateSegment: function(index)
 * - onEnhanceSegmentPrompt: function(index)
 * - onEnhanceAllPrompts: function
 * - onAddSegment: function
 * - onSetStep: function(step)
 * - onReferenceChange: function(items)
 * - dragHandlers: object – { onDragStart, onDragOver, onDragLeave, onDrop, onDragEnd, draggedIndex, dragOverIndex }
 */
export default function StoryboardStep({
    montage,
    loading,
    enhancingPrompts,
    onUpdate,
    onGenerateStoryboard,
    onUpdateSegment,
    onDeleteSegment,
    onRegenerateSegment,
    onEnhanceSegmentPrompt,
    onEnhanceAllPrompts,
    onAddSegment,
    onSetStep,
    onReferenceChange,
    dragHandlers,
}) {
    const segments = montage.json_data.segments;

    return (
        <div className="montage-step-content">
            <h2>📝 {t("montage.step.storyboard")}</h2>
            <p className="montage-step-description">
                {t("montage.storyboard.desc")}
            </p>

            <div className="montage-storyboard-form">
                <div className="montage-form-group">
                    <label>{t("montage.label.projectDesc")}</label>
                    <textarea
                        value={montage.description}
                        onChange={(e) => onUpdate("description", e.target.value)}
                        placeholder={t("montage.placeholder.desc")}
                        rows={6}
                        className="montage-textarea"
                    />
                </div>

                <div className="montage-form-row">
                    <div className="montage-form-group">
                        <label>{t("montage.label.targetDuration")}</label>
                        <input
                            type="number"
                            value={montage.target_duration}
                            onChange={(e) => onUpdate("target_duration", parseInt(e.target.value) || 30)}
                            min={5}
                            max={120}
                            className="montage-input"
                        />
                    </div>

                    <div className="montage-form-group">
                        <label>{t("montage.label.segmentCount")}</label>
                        <input
                            type="number"
                            value={Math.max(2, Math.round(montage.target_duration / 10))}
                            disabled
                            className="montage-input"
                            title="Auto-calculé (~10s par segment)"
                        />
                    </div>
                </div>

                <button
                    type="button"
                    className="montage-btn montage-btn-primary"
                    disabled={!montage.description.trim() || loading || !montage.montage_id}
                    onClick={onGenerateStoryboard}
                >
                    {loading ? t("montage.btn.generating") : t("montage.btn.generateStoryboard")}
                </button>
                {!montage.montage_id && (
                    <p className="montage-hint">
                        {t("montage.hint.createFirst")}
                    </p>
                )}
            </div>

            <div className="montage-reference-panel">
                <MediaSelector
                    title={t("montage.label.refMedia")}
                    selected={montage.json_data.reference_media || []}
                    onChange={onReferenceChange}
                    maxCount={8}
                    allowUpload
                    allowLibrary
                    pickerTarget="montage_ref"
                    pickerReturn={montage.montage_id ? `/montage?id=${montage.montage_id}` : "/montage"}
                    pickerMulti
                    gridLimit={8}
                />
            </div>

            {segments.length > 0 && (
                <div className="montage-segments-list">
                    <div className="montage-segments-header">
                        <h3>{t("montage.label.segmentsGenerated")} ({segments.length})</h3>
                        <div className="montage-segments-actions-header">
                            <button
                                type="button"
                                className="montage-btn-small montage-btn-outline"
                                onClick={onEnhanceAllPrompts}
                                disabled={enhancingPrompts || loading}
                                title={t("montage.btn.enhanceAllTitle")}
                            >
                                {enhancingPrompts ? t("montage.btn.enhancing") : t("montage.btn.enhanceAll")}
                            </button>
                            <span className="montage-segments-total">
                                {t("montage.label.totalDuration")} : {montage.json_data.total_duration || 0}s
                            </span>
                        </div>
                    </div>

                    {segments.map((segment, index) => (
                        <SegmentCard
                            key={segment.segment_id || index}
                            segment={segment}
                            index={index}
                            montageId={montage.montage_id || ""}
                            aspectRatio={montage.aspect_ratio || ""}
                            onUpdate={(updatedSegment) => onUpdateSegment(index, updatedSegment)}
                            onDelete={() => onDeleteSegment(index)}
                            onRegenerate={() => onRegenerateSegment(index)}
                            onEnhancePrompt={() => onEnhanceSegmentPrompt(index)}
                            readonly={false}
                            draggable={true}
                            onDragStart={(e) => dragHandlers.onDragStart(e, index)}
                            onDragOver={(e) => dragHandlers.onDragOver(e, index)}
                            onDragLeave={dragHandlers.onDragLeave}
                            onDrop={(e) => dragHandlers.onDrop(e, index)}
                            onDragEnd={dragHandlers.onDragEnd}
                            isDragging={dragHandlers.draggedIndex === index}
                            isDragOver={dragHandlers.dragOverIndex === index}
                        />
                    ))}

                    <div className="montage-segments-actions">
                        <button
                            type="button"
                            className="montage-btn montage-btn-outline"
                            onClick={onAddSegment}
                        >
                            {t("montage.btn.addSegment")}
                        </button>
                        <button
                            type="button"
                            className="montage-btn montage-btn-secondary"
                            onClick={() => onSetStep("images")}
                        >
                            {t("montage.btn.nextImages")}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
